import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Divider, Form, Input, Row, Select } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../../../../auth/hooks";
import { Client } from "../../../../../../client/types";
import { PaymentFrequency } from "../../../../../../contract/enums";
import { CalcType } from "../../../../../enums";
import {
  AnimalReinsuranceAmount,
  ElementReinsuranceAmount,
  GapDuration,
  GlassReinsuranceAmount,
  InjuryReinsuranceAmount,
  ReplacementVehicleCategory,
  SmallDamageReinsuranceAmount,
  TotalDamageReinsuranceAmount,
} from "../../../enums";
import { VehicleCalcForm } from "../../../types";
import {
  isCrossSellingDiscountAvailable,
  isReinsuranceAvailable,
  resolveGapCoverageLimitOptions,
} from "../../../utils";

const colSpan = 8;

interface Props {
  holder?: Partial<Client>;
}

export const VehicleCalcGeneralAndReinsurancesDataSection = ({ holder }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const form = Form.useFormInstance<VehicleCalcForm>();
  const firstRegistrationDate = Form.useWatch(["vehicleData", "firstRegistrationDate"], form);
  const calcType = Form.useWatch("type", form);
  const holderRegistrationDate = Form.useWatch(["clientsData", "holderRegistrationDate"], form);
  const category = Form.useWatch(["vehicleData", "category"], form);
  const fuelType = Form.useWatch(["vehicleData", "fuelType"], form);
  const glass = Form.useWatch(["reinsurancesData", "glass"], form);
  const animal = Form.useWatch(["reinsurancesData", "animal"], form);
  const element = Form.useWatch(["reinsurancesData", "element"], form);
  const injury = Form.useWatch(["reinsurancesData", "injury"], form);
  const gap = Form.useWatch(["reinsurancesData", "gap"], form);
  const replacementVehicle = Form.useWatch(["reinsurancesData", "replacementVehicle"], form);
  const smallDamage = Form.useWatch(["reinsurancesData", "uniqaSmallDamage"], form);
  const totalDamage = Form.useWatch(["reinsurancesData", "uniqaTotalDamage"], form);
  const uniqaContract = Form.useWatch(["generalData", "crossSelling", "uniqaContract"], form);

  const showElementAmount = element && calcType === CalcType.MTPL;
  const showInjuryAmount = injury && calcType === CalcType.MTPL;

  return (
    <Card type="inner" className="card-box" title={t("calc.vehicle.sections.generalData")}>
      <Row gutter={rowGutter}>
        <Col span={12}>
          <Divider className="divider-subheader">{t("calc.vehicle.sections.generalInfo")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "effectiveBeginningDate"]}
                label={t("calc.vehicle.attrs.generalData.effectiveBeginningDate")}
                rules={[
                  validations.notNull,
                  validations.dateInInterval(
                    !!firstRegistrationDate && toDate(firstRegistrationDate)?.isAfter(dayjs(), "day")
                      ? firstRegistrationDate
                      : dayjs(),
                    dayjs().add(1, "year"),
                  ),
                ]}
                {...datePickerFormItemProps}
              >
                <DatePicker {...datePickerStandardProps} minDate={dayjs()} />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "paymentFrequency"]}
                label={t("contract.enums.paymentFrequency._label")}
                rules={[validations.notNull]}
              >
                <Select
                  {...selectStandardProps}
                  options={[PaymentFrequency.ANNUALLY, PaymentFrequency.SEMI_ANNUALLY, PaymentFrequency.QUARTERLY].map(
                    (frequency) => ({
                      value: frequency,
                      label: t("contract.enums.paymentFrequency." + frequency),
                    }),
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Divider className="divider-subheader">{t("calc.vehicle.sections.gapInfo")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "gapDuration"]}
                label={t("calc.vehicle.enums.gapDuration._label")}
                rules={[validations.none]}
              >
                <Select
                  {...selectStandardProps}
                  allowClear
                  disabled={calcType === CalcType.MTPL}
                  options={[
                    GapDuration.MONTHS_12,
                    GapDuration.MONTHS_24,
                    GapDuration.MONTHS_36,
                    GapDuration.MONTHS_48,
                    GapDuration.MONTHS_60,
                  ].map((duration) => ({
                    value: duration,
                    label: t("calc.vehicle.enums.gapDuration." + duration),
                  }))}
                />
              </Form.Item>
            </Col>

            {!isTopAgentPlus && (
              <Col span={colSpan}>
                <Form.Item
                  name={["generalData", "gapCoverageLimit"]}
                  label={t("calc.vehicle.enums.gapCoverageLimit._label")}
                  rules={[validations.none]}
                >
                  <Select
                    {...selectStandardProps}
                    allowClear
                    disabled={calcType === CalcType.MTPL}
                    options={resolveGapCoverageLimitOptions(toDate(holderRegistrationDate)).map((coverageLimit) => ({
                      value: coverageLimit,
                      label: t("calc.vehicle.enums.gapCoverageLimit." + coverageLimit),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={12}>
          <Divider className="divider-subheader">{t("calc.vehicle.sections.reinsurancesData")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "extendedAssistance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("extendedAssistance", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.extendedAssistance")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "theft"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("theft", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.theft")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "vandalism"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("vandalism", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.vandalism")}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "glass"]}
                className={glass ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox
                  disabled={!isReinsuranceAvailable("glass", calcType, category, fuelType)}
                  onChange={() => form.setFieldValue(["reinsurancesData", "glassAmount"], undefined)}
                >
                  {t("calc.vehicle.attrs.reinsurancesData.glass")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              {glass && (
                <Form.Item
                  name={["reinsurancesData", "glassAmount"]}
                  label={t("calc.vehicle.attrs.reinsurancesData.glassAmountLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(GlassReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.vehicle.enums.glassReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "animal"]}
                className={animal ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox
                  disabled={!isReinsuranceAvailable("animal", calcType, category, fuelType)}
                  onChange={() => form.setFieldValue(["reinsurancesData", "animalAmount"], undefined)}
                >
                  {t("calc.vehicle.attrs.reinsurancesData.animal")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              {animal && (
                <Form.Item
                  name={["reinsurancesData", "animalAmount"]}
                  label={t("calc.vehicle.attrs.reinsurancesData.animalAmountLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(AnimalReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.vehicle.enums.animalReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "element"]}
                className={showElementAmount ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox
                  disabled={!isReinsuranceAvailable("element", calcType, category, fuelType)}
                  onChange={() => form.setFieldValue(["reinsurancesData", "elementAmount"], undefined)}
                >
                  {t("calc.vehicle.attrs.reinsurancesData.element")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              {showElementAmount && (
                <Form.Item
                  name={["reinsurancesData", "elementAmount"]}
                  label={t("calc.vehicle.attrs.reinsurancesData.elementAmountLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(ElementReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.vehicle.enums.elementReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "injury"]}
                className={showInjuryAmount ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox
                  disabled={!isReinsuranceAvailable("injury", calcType, category, fuelType)}
                  onChange={() => form.setFieldValue(["reinsurancesData", "injuryAmount"], undefined)}
                >
                  {t("calc.vehicle.attrs.reinsurancesData.injury")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              {showInjuryAmount && (
                <Form.Item
                  name={["reinsurancesData", "injuryAmount"]}
                  label={t("calc.vehicle.attrs.reinsurancesData.injuryAmountLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(InjuryReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.vehicle.enums.injuryReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "gap"]}
                className={gap ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("gap", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.gap")}
                </Checkbox>
              </Form.Item>
            </Col>

            {gap && (
              <>
                <Col span={colSpan}>
                  <Form.Item
                    name={["reinsurancesData", "gapDuration"]}
                    label={t("calc.vehicle.enums.gapDuration._label")}
                    rules={[validations.notNull]}
                  >
                    <Select
                      {...selectStandardProps}
                      options={[
                        GapDuration.MONTHS_24,
                        GapDuration.MONTHS_36,
                        GapDuration.MONTHS_48,
                        GapDuration.MONTHS_60,
                      ].map((duration) => ({
                        value: duration,
                        label: t("calc.vehicle.enums.gapDuration." + duration),
                      }))}
                    />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["reinsurancesData", "gapComplicityReinsurance"]}
                    className="form-item-without-label"
                    valuePropName="checked"
                    rules={[validations.none]}
                    initialValue={false}
                  >
                    <Checkbox style={{ maxHeight: 22 }}>
                      {t("calc.vehicle.attrs.reinsurancesData.gapComplicityReinsurance")}
                    </Checkbox>
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "replacementVehicle"]}
                className={replacementVehicle ? "form-item-without-label" : undefined}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("replacementVehicle", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.replacementVehicle")}
                </Checkbox>
              </Form.Item>
            </Col>

            {replacementVehicle && (
              <Col span={colSpan}>
                <Form.Item
                  name={["reinsurancesData", "replacementVehicleCategory"]}
                  label={t("calc.vehicle.enums.replacementVehicleCategory._label")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(ReplacementVehicleCategory).map((category) => ({
                      value: category,
                      label: t("calc.vehicle.enums.replacementVehicleCategory." + category),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "generaliAbroadVehicleRepair"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox
                  disabled={!isReinsuranceAvailable("generaliAbroadVehicleRepair", calcType, category, fuelType)}
                >
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.reinsurancesData.generaliAbroadVehicleRepair")}
                    tooltip={t("calc.vehicle.helpers.generaliAbroadVehicleRepairDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "koopExtendedWarranty"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("koopExtendedWarranty", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.koopExtendedWarranty")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "generaliElectroPackage"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("generaliElectroPackage", calcType, category, fuelType)}>
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.reinsurancesData.generaliElectroPackage")}
                    tooltip={t("calc.vehicle.helpers.generaliElectroPackageDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Divider className="divider-subheader">
            {t("calc.vehicle.sections.additionalReinsurances")}
            <LabelWithTooltip
              tooltip={t("calc.vehicle.helpers.additionalReinsurancesDesc")}
              className="margin-left-tiny"
            />
          </Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSmallDamage"]}
                className={classNames({ "form-item-without-label": smallDamage })}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("uniqaSmallDamage", calcType, category, fuelType)}>
                  <LabelWithTooltip
                    tooltip={t("calc.vehicle.helpers.uniqaSmallDamageDesc")}
                    label={t("calc.vehicle.attrs.reinsurancesData.uniqaSmallDamage")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              {smallDamage && (
                <Form.Item
                  name={["reinsurancesData", "uniqaSmallDamageAmount"]}
                  label={t("calc.vehicle.attrs.reinsurancesData.uniqaSmallDamageAmountLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(SmallDamageReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.vehicle.enums.smallDamageReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={9}>
              <Form.Item
                name={["reinsurancesData", "uniqaTotalDamage"]}
                className={classNames({ "form-item-without-label": totalDamage })}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("uniqaTotalDamage", calcType, category, fuelType)}>
                  <LabelWithTooltip
                    tooltip={t("calc.vehicle.helpers.uniqaTotalDamageDesc")}
                    label={t("calc.vehicle.attrs.reinsurancesData.uniqaTotalDamage")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              {totalDamage && (
                <Form.Item
                  name={["reinsurancesData", "uniqaTotalDamageAmount"]}
                  label={t("calc.vehicle.attrs.reinsurancesData.uniqaTotalDamageAmountLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    allowClear
                    options={Object.keys(TotalDamageReinsuranceAmount).map((amount) => ({
                      value: amount,
                      label: t("calc.vehicle.enums.totalDamageReinsuranceAmount." + amount),
                    }))}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaLawyerAssistance"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("uniqaLawyerAssistance", calcType, category, fuelType)}>
                  <LabelWithTooltip
                    tooltip={t("calc.vehicle.helpers.uniqaLawyerAssistanceDesc")}
                    label={t("calc.vehicle.attrs.reinsurancesData.uniqaLawyerAssistance")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaSidecarMtpl"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("uniqaSidecarMtpl", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.uniqaSidecarMtpl")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["reinsurancesData", "uniqaChildrenInjury"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isReinsuranceAvailable("uniqaChildrenInjury", calcType, category, fuelType)}>
                  {t("calc.vehicle.attrs.reinsurancesData.uniqaChildrenInjury")}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider className="divider-subheader">{t("calc.vehicle.sections.crossSelling")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "crossSelling", "kooperativaContract"]}
                className="form-item-without-label"
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isCrossSellingDiscountAvailable("kooperativaContract", calcType, holder?.type)}>
                  {t("calc.vehicle.attrs.generalData.crossSelling.kooperativaContract")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                className="form-item-without-label"
                name={["generalData", "crossSelling", "unionHealthContract"]}
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox disabled={!isCrossSellingDiscountAvailable("unionHealthContract", calcType, holder?.type)}>
                  {t("calc.vehicle.attrs.generalData.crossSelling.unionHealthContract")}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "crossSelling", "pillowDiscountCode"]}
                label={t("calc.vehicle.attrs.generalData.crossSelling.pillowDiscountCode")}
                rules={[validations.size(1, 64)]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name={["generalData", "crossSelling", "uniqaContract"]}
                valuePropName="checked"
                className={classNames({ "form-item-without-label": uniqaContract })}
                rules={[validations.none]}
                initialValue={false}
              >
                <Checkbox style={uniqaContract ? { maxHeight: 22 } : undefined}>
                  {t("calc.vehicle.attrs.generalData.crossSelling.uniqaContract")}
                </Checkbox>
              </Form.Item>
            </Col>

            {uniqaContract && (
              <Col span={colSpan}>
                <Form.Item
                  name={["generalData", "crossSelling", "uniqaContractNumber"]}
                  label={t("calc.vehicle.attrs.generalData.crossSelling.uniqaContractNumber")}
                  rules={[validations.notBlank, validations.size(1, 64)]}
                >
                  <Input />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Card>
  );
};
